/**
 * Triple Whale tab — Platforms group.
 * Internal tabs: Overview / Channels / Journeys / Sync.
 *
 * Reuses Shopify.css `.shop-*` classes for layout + KPI grid + chart panels.
 * Triple-Whale-specific styling lives in TripleWhale.css.
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { motion } from 'framer-motion';
import {
  BarChart3, Activity, Layers as ChannelIcon, Database,
  RefreshCw, DollarSign, ShoppingBag, TrendingUp, TrendingDown,
  Users as UsersIcon, ArrowUp, ArrowDown, Percent,
  ChevronLeft, ChevronRight, ShoppingCart, CreditCard,
} from 'lucide-react';
import {
  Area, ComposedChart, Line, LineChart, BarChart, Bar,
  PieChart, Pie, Cell,
  ResponsiveContainer, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
} from 'recharts';
import { useToast } from './ToastContainer';
import api from '../utils/api';
import './Shopify.css';
import './TripleWhale.css';

const PERIODS = [
  { value: '7d',  label: '7 days' },
  { value: '30d', label: '30 days' },
  { value: '90d', label: '90 days' },
];

const TABS = [
  { value: 'overview', label: 'Overview', icon: BarChart3 },
  { value: 'channels', label: 'Channels', icon: ChannelIcon },
  { value: 'journeys', label: 'Journeys', icon: Activity },
  { value: 'sync',     label: 'Sync',     icon: Database },
];

const moneyFmt = (amount, code) => {
  if (amount == null) return '—';
  try {
    return new Intl.NumberFormat('en-US', {
      style: 'currency', currency: code || 'SAR', maximumFractionDigits: 0,
    }).format(Number(amount));
  } catch {
    return `${code || ''} ${Number(amount).toLocaleString()}`;
  }
};
const intFmt = (n) => (n == null ? '—' : Number(n).toLocaleString());
const decFmt = (n, digits = 2) => (n == null ? '—' : Number(n).toFixed(digits));
const pctFmt = (n) => (n == null ? '—' : `${Number(n).toFixed(1)}%`);
const relativeTime = (iso) => {
  if (!iso) return 'never';
  const ms = Date.now() - new Date(iso).getTime();
  if (ms < 60_000) return 'just now';
  if (ms < 3_600_000) return `${Math.round(ms / 60_000)} min ago`;
  if (ms < 86_400_000) return `${Math.round(ms / 3_600_000)} h ago`;
  return `${Math.round(ms / 86_400_000)} d ago`;
};

const TripleWhale = () => {
  const { showError, showSuccess } = useToast();

  const [accounts, setAccounts] = useState([]);
  const [selectedAccountId, setSelectedAccountId] = useState(null);
  const [period, setPeriod] = useState('30d');
  const [activeTab, setActiveTab] = useState('overview');

  const [overview, setOverview] = useState(null);
  const [syncJobs, setSyncJobs] = useState([]);

  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [triggering, setTriggering] = useState(false);

  // Accounts list — fetched once
  useEffect(() => {
    api.get('/api/triplewhale/accounts')
      .then(res => {
        const list = res.data.accounts || [];
        setAccounts(list);
        if (list.length === 1) setSelectedAccountId(list[0].id);
      })
      .catch(e => showError('Failed to load Triple Whale accounts: ' + (e.response?.data?.error || e.message)));
  }, [showError]);

  const loadHeader = useCallback(async () => {
    const accountQs = selectedAccountId ? `&accountId=${selectedAccountId}` : '';
    const settled = await Promise.allSettled([
      api.get(`/api/triplewhale/overview?period=${period}${accountQs}`),
      api.get(`/api/triplewhale/sync-status?${accountQs.replace('&', '')}`),
    ]);
    if (settled[0].status === 'fulfilled') setOverview(settled[0].value.data);
    if (settled[1].status === 'fulfilled') setSyncJobs(settled[1].value.data.jobs || []);
  }, [selectedAccountId, period]);

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    loadHeader().finally(() => { if (mounted) setLoading(false); });
    return () => { mounted = false; };
  }, [loadHeader]);

  const handleRefresh = async () => {
    setRefreshing(true);
    await loadHeader();
    setRefreshing(false);
  };

  const handleTriggerSync = async () => {
    if (!accounts.length) { showError('No Triple Whale account configured'); return; }
    const acct = selectedAccountId
      ? accounts.find(a => a.id === selectedAccountId)
      : accounts[0];
    if (!acct) return;
    if (!window.confirm(`Queue a 90-day backfill for ${acct.display_name || acct.account_handle}?`)) return;
    setTriggering(true);
    try {
      const today = new Date().toISOString().slice(0, 10);
      const start = new Date(Date.now() - 90 * 86400 * 1000).toISOString().slice(0, 10);
      const res = await api.post('/api/triplewhale/trigger-sync', {
        accountId: acct.id,
        jobType: 'manual',
        startDate: start,
        endDate: today,
      });
      showSuccess(res.data.message || 'Sync queued');
      await loadHeader();
    } catch (e) {
      showError('Trigger failed: ' + (e.response?.data?.error || e.message));
    } finally {
      setTriggering(false);
    }
  };

  const selectedAccount = useMemo(
    () => accounts.find(a => a.id === selectedAccountId) || accounts[0] || null,
    [accounts, selectedAccountId]
  );
  const activeJob = useMemo(
    () => syncJobs.find(j => j.status === 'pending' || j.status === 'running') || null,
    [syncJobs]
  );
  const lastSync = useMemo(() => {
    if (!selectedAccount) return null;
    const a = selectedAccount.last_summary_sync_at;
    const b = selectedAccount.last_journeys_sync_at;
    if (!a) return b;
    if (!b) return a;
    return new Date(a) > new Date(b) ? a : b;
  }, [selectedAccount]);

  const currency = overview?.currency || selectedAccount?.currency_code || 'SAR';

  if (loading) {
    return (
      <div className="shop-loading">
        <div className="shop-spinner" />
        <p>Loading Triple Whale data…</p>
      </div>
    );
  }

  return (
    <div className="shop">
      <div className="shop-header">
        <div>
          <h1 className="shop-title">Triple Whale</h1>
          <p className="shop-subtitle">Blended attribution, ad spend, and pixel-tracked customer journeys</p>
        </div>
        <div className="shop-header-actions">
          {accounts.length > 1 && (
            <select
              className="shop-select"
              value={selectedAccountId || ''}
              onChange={(e) => setSelectedAccountId(e.target.value || null)}
            >
              {accounts.map(a => (
                <option key={a.id} value={a.id}>{a.display_name || a.account_handle}</option>
              ))}
            </select>
          )}
          <div className="shop-period-toggle">
            {PERIODS.map(p => (
              <button
                key={p.value}
                className={`shop-period-btn ${period === p.value ? 'active' : ''}`}
                onClick={() => setPeriod(p.value)}
              >{p.label}</button>
            ))}
          </div>
          <button className="shop-refresh" onClick={handleRefresh} disabled={refreshing}>
            <RefreshCw size={14} className={refreshing ? 'shop-spin' : ''} />
            {refreshing ? 'Refreshing…' : 'Refresh'}
          </button>
        </div>
      </div>

      <div className="shop-sync-bar">
        <div className="shop-sync-info">
          {activeJob ? (
            <>
              <span className="shop-sync-dot active" />
              <span><strong>{activeJob.job_type.replace('_', ' ')}</strong> {activeJob.status}
                {' · '}started {relativeTime(activeJob.started_at || activeJob.created_at)}
              </span>
            </>
          ) : (
            <>
              <span className="shop-sync-dot" />
              <span>Last synced {relativeTime(lastSync)}</span>
            </>
          )}
        </div>
        <button
          className="shop-action-btn"
          onClick={handleTriggerSync}
          disabled={triggering || !!activeJob}
          title={activeJob ? 'A sync is already in progress' : 'Queue a 90-day backfill'}
        >
          {triggering ? 'Queueing…' : (activeJob ? 'Sync running…' : 'Backfill 90d')}
        </button>
      </div>

      <div className="shop-tabs">
        {TABS.map(t => (
          <button
            key={t.value}
            className={`shop-tab ${activeTab === t.value ? 'active' : ''}`}
            onClick={() => setActiveTab(t.value)}
          >
            <t.icon size={16} /> {t.label}
          </button>
        ))}
      </div>

      {activeTab === 'overview' && (
        <OverviewTab overview={overview} period={period} currency={currency} />
      )}
      {activeTab === 'channels' && (
        <ChannelsTab overview={overview} currency={currency} />
      )}
      {activeTab === 'journeys' && (
        <JourneysTab accountId={selectedAccountId} currency={currency} />
      )}
      {activeTab === 'sync' && (
        <SyncTab jobs={syncJobs} />
      )}
    </div>
  );
};


// ============================================================
// Overview tab
// ============================================================
const OverviewTab = ({ overview, period, currency }) => {
  if (!overview) return <div className="shop-empty">No data yet — run a backfill to populate.</div>;
  const k = overview.kpis || {};
  const daily = overview.daily || [];
  const funnel = overview.funnel || {};
  const mix = overview.customerMix || {};
  return (
    <>
      {/* Hero ROAS panel — radial gauge + sparkline + sub-stats */}
      <HeroRoasPanel k={k} daily={daily} period={period} currency={currency} />

      {/* Sparkline KPI strip — small tiles with inline trends */}
      <div className="tw-spark-grid">
        <SparkTile
          label="Revenue"
          value={moneyFmt(k.revenue?.value, currency)}
          delta={k.revenue?.delta}
          series={daily.map(d => d.revenue)}
          color="#10b981"
          icon={<DollarSign size={14} />}
        />
        <SparkTile
          label="Ad Spend"
          value={moneyFmt(k.adSpend?.value, currency)}
          delta={k.adSpend?.delta}
          invertDelta
          series={daily.map(d => d.adSpend)}
          color="#f59e0b"
          icon={<TrendingDown size={14} />}
        />
        <SparkTile
          label="MER"
          value={pctFmt(k.mer?.value)}
          delta={k.mer?.delta}
          series={daily.map(d => d.mer)}
          color="#22d3ee"
          icon={<Percent size={14} />}
        />
        <SparkTile
          label="CAC"
          value={moneyFmt(k.cac?.value, currency)}
          delta={k.cac?.delta}
          invertDelta
          series={daily.map(d => d.cac)}
          color="#a78bfa"
          icon={<UsersIcon size={14} />}
        />
        <SparkTile
          label="AOV"
          value={moneyFmt(k.aov?.value, currency)}
          delta={k.aov?.delta}
          series={daily.map(d => d.aov)}
          color="#60a5fa"
          icon={<TrendingUp size={14} />}
        />
        <SparkTile
          label="Orders"
          value={intFmt(k.orders?.value)}
          delta={k.orders?.delta}
          series={daily.map(d => d.orders)}
          color="#34d399"
          icon={<ShoppingBag size={14} />}
          variant="bar"
        />
      </div>

      {/* Funnel + Customer mix donut + Gross profit bar */}
      <div className="tw-row-3">
        <FunnelWidget funnel={funnel} />
        <CustomerMixDonut mix={mix} currency={currency} />
        <ProfitWidget
          revenue={k.revenue?.value}
          grossProfit={k.grossProfit?.value}
          newRev={k.newCustomerRevenue?.value}
          returningRev={k.returningRevenue?.value}
          currency={currency}
        />
      </div>

      <div className="shop-panel">
        <div className="shop-panel-head">
          <h3>Revenue vs Ad Spend</h3>
          <span className="shop-panel-meta">{period} · daily</span>
        </div>
        <div className="shop-chart-box">
          {overview.daily?.length ? (
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={overview.daily}>
                <defs>
                  <linearGradient id="twRev" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%"   stopColor="#10b981" stopOpacity={0.5} />
                    <stop offset="100%" stopColor="#10b981" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.08)" />
                <XAxis dataKey="date" tick={{ fill: 'rgba(255,255,255,0.6)', fontSize: 11 }} />
                <YAxis tick={{ fill: 'rgba(255,255,255,0.6)', fontSize: 11 }} />
                <Tooltip contentStyle={chartTooltipStyle} formatter={(v) => moneyFmt(v, currency)} />
                <Legend wrapperStyle={{ fontSize: 11, color: 'rgba(255,255,255,0.7)' }} />
                <Area type="monotone" dataKey="revenue" stroke="#10b981" strokeWidth={2} fill="url(#twRev)" name="Revenue" />
                <Line type="monotone" dataKey="adSpend" stroke="#38bdf8" strokeWidth={2} dot={false} name="Ad Spend" />
              </ComposedChart>
            </ResponsiveContainer>
          ) : <div className="shop-empty">No daily data in this window. Run a backfill.</div>}
        </div>
      </div>

      <div className="shop-grid-2">
        <div className="shop-panel">
          <div className="shop-panel-head">
            <h3>Top first-touch sources</h3>
            <span className="shop-panel-meta">orders + revenue</span>
          </div>
          {(overview.attributionSources || []).length === 0 ? (
            <div className="shop-empty">No journey data yet.</div>
          ) : (
            <div>
              {overview.attributionSources.map((s) => (
                <div key={s.source} className="tw-source-row">
                  <span className={`tw-source-name ${s.source === 'Unknown' ? 'unknown' : ''}`}>{s.source}</span>
                  <span className="tw-source-orders">{intFmt(s.orders)}</span>
                  <span className="tw-source-revenue">{moneyFmt(s.revenue, currency)}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="shop-panel">
          <div className="shop-panel-head">
            <h3>Spend by channel</h3>
            <span className="shop-panel-meta">paid only</span>
          </div>
          {(overview.byChannel || []).length === 0 ? (
            <div className="shop-empty">No channel data.</div>
          ) : (
            <ChannelBars channels={overview.byChannel} currency={currency} />
          )}
        </div>
      </div>
    </>
  );
};


// ============================================================
// Channels tab — fuller breakdown
// ============================================================
const ChannelsTab = ({ overview, currency }) => {
  if (!overview) return <div className="shop-empty">No data yet.</div>;
  const channels = overview.byChannel || [];
  if (channels.length === 0) {
    return <div className="shop-empty">No paid channels with spend in this window.</div>;
  }
  const totalSpend = channels.reduce((s, c) => s + (c.spend || 0), 0);
  return (
    <div className="shop-panel">
      <div className="shop-panel-head">
        <h3>Paid channel performance</h3>
        <span className="shop-panel-meta">total spend {moneyFmt(totalSpend, currency)}</span>
      </div>
      <ChannelBars channels={channels} currency={currency} />
    </div>
  );
};

const ChannelBars = ({ channels, currency }) => {
  const max = Math.max(...channels.map(c => c.spend || 0), 1);
  return (
    <div>
      {channels.map((c) => {
        const pct = max > 0 ? (c.spend / max) * 100 : 0;
        const roasClass = c.roas >= 3 ? '' : (c.roas >= 1.5 ? 'low' : 'bad');
        return (
          <div key={c.channel} className="tw-channel-row">
            <span className="tw-channel-name">{c.channel}</span>
            <div className="tw-channel-bar-wrap">
              <div className="tw-channel-bar-fill" style={{ width: `${pct}%` }} />
            </div>
            <span className="tw-channel-spend">{moneyFmt(c.spend, currency)}</span>
            <span className={`tw-channel-roas ${roasClass}`}>{decFmt(c.roas, 2)}x</span>
          </div>
        );
      })}
    </div>
  );
};


// ============================================================
// Journeys tab — paginated table
// ============================================================
const JourneysTab = ({ accountId, currency }) => {
  const { showError } = useToast();
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [pageSize] = useState(25);
  const [loading, setLoading] = useState(true);

  useEffect(() => { setPage(0); }, [accountId]);

  useEffect(() => {
    setLoading(true);
    const params = new URLSearchParams({ page, pageSize });
    if (accountId) params.set('accountId', accountId);
    api.get(`/api/triplewhale/journeys?${params}`)
      .then(r => {
        setRows(r.data.orders || []);
        setTotal(r.data.total || 0);
      })
      .catch(e => showError('Failed to load journeys: ' + (e.response?.data?.error || e.message)))
      .finally(() => setLoading(false));
  }, [accountId, page, pageSize, showError]);

  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  return (
    <div className="shop-panel">
      <div className="shop-panel-head">
        <h3>Pixel-attributed orders</h3>
        <span className="shop-panel-meta">{intFmt(total)} total</span>
      </div>
      {loading ? (
        <div className="shop-loading" style={{ minHeight: 200 }}><div className="shop-spinner" /></div>
      ) : rows.length === 0 ? (
        <div className="shop-empty">No journeys yet — run a backfill on the Sync tab.</div>
      ) : (
        <>
          <table className="shop-table">
            <thead>
              <tr>
                <th>Order</th>
                <th>Date</th>
                <th>First touch</th>
                <th>Last touch</th>
                <th>Touches</th>
                <th style={{ textAlign: 'right' }}>Revenue</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(r => (
                <tr key={r.orderId}>
                  <td><code style={{ fontSize: '0.78rem', color: 'rgba(255,255,255,0.6)' }}>{r.orderId}</code></td>
                  <td>{r.orderDate}</td>
                  <td>
                    <span className={`tw-channel-pill ${r.firstTouchChannel || 'other'}`}>
                      {r.firstTouchSource || '—'}
                    </span>
                  </td>
                  <td>
                    <span className={`tw-channel-pill ${r.lastTouchChannel || 'other'}`}>
                      {r.lastTouchSource || '—'}
                    </span>
                  </td>
                  <td>{intFmt(r.touchCount)}</td>
                  <td style={{ textAlign: 'right', fontWeight: 600, color: '#10b981' }}>
                    {moneyFmt(r.totalPrice, r.currency || currency)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="shop-pagination">
            <button
              className="shop-pag-btn"
              onClick={() => setPage(p => Math.max(0, p - 1))}
              disabled={page === 0}
            >
              <ChevronLeft size={14} /> Prev
            </button>
            <span className="shop-pag-info">page {page + 1} of {totalPages}</span>
            <button
              className="shop-pag-btn"
              onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
              disabled={page + 1 >= totalPages}
            >
              Next <ChevronRight size={14} />
            </button>
          </div>
        </>
      )}
    </div>
  );
};


// ============================================================
// Sync tab — recent jobs
// ============================================================
const SyncTab = ({ jobs }) => {
  if (!jobs?.length) return <div className="shop-empty">No sync jobs yet.</div>;
  return (
    <div className="shop-panel">
      <div className="shop-panel-head">
        <h3>Recent sync jobs</h3>
        <span className="shop-panel-meta">last {jobs.length}</span>
      </div>
      <table className="shop-table">
        <thead>
          <tr>
            <th>Type</th>
            <th>Status</th>
            <th>Window</th>
            <th>Started</th>
            <th>Counters</th>
            <th>By</th>
          </tr>
        </thead>
        <tbody>
          {jobs.map(j => (
            <tr key={j.id}>
              <td>{j.job_type.replace('_', ' ')}</td>
              <td>
                <span className={`tw-channel-pill ${
                  j.status === 'completed' ? 'organic' :
                  j.status === 'running' ? 'paid' :
                  j.status === 'failed' ? 'other' : 'direct'
                }`}>
                  {j.status}
                </span>
              </td>
              <td>{j.start_date || '—'} → {j.end_date || '—'}</td>
              <td>{relativeTime(j.started_at || j.created_at)}</td>
              <td style={{ fontFamily: 'monospace', fontSize: '0.78rem', color: 'rgba(255,255,255,0.6)' }}>
                {j.counters && Object.keys(j.counters).length
                  ? JSON.stringify(j.counters).slice(0, 80)
                  : '—'}
              </td>
              <td style={{ color: 'rgba(255,255,255,0.6)' }}>{j.created_by || '—'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};


// ============================================================
// Hero ROAS panel — big number + radial gauge + sparkline + sub-stats
// ============================================================
const HeroRoasPanel = ({ k, daily, period, currency }) => {
  const roas = Number(k.blendedRoas?.value) || 0;
  const merVal = Number(k.mer?.value) || 0;
  const adSpend = Number(k.adSpend?.value) || 0;
  const revenue = Number(k.revenue?.value) || 0;

  // Gauge: roas of 0..6+ mapped to 0..180deg.
  // Bands:  red < 1.5 / amber 1.5–3 / green > 3.
  const target = 6;
  const pct = Math.min(roas / target, 1);
  const angle = pct * 180; // 0 at left, 180 at right
  const gaugeColor =
    roas >= 3   ? '#10b981' :
    roas >= 1.5 ? '#fbbf24' :
                  '#f87171';

  const sparkData = (daily || []).map(d => ({ date: d.date, roas: d.roas }));

  return (
    <motion.div
      className="tw-hero-panel"
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.04 }}
    >
      <div className="tw-hero-left">
        <div className="tw-hero-label">Blended ROAS · {period}</div>
        <div className="tw-hero-value" style={{ color: gaugeColor }}>
          {decFmt(roas, 2)}<span className="tw-hero-x">x</span>
          <DeltaPill delta={k.blendedRoas?.delta} />
        </div>
        <div className="tw-hero-sparkbox">
          {sparkData.length > 1 ? (
            <ResponsiveContainer width="100%" height={56}>
              <LineChart data={sparkData}>
                <Line type="monotone" dataKey="roas" stroke={gaugeColor} strokeWidth={2} dot={false} />
                <Tooltip
                  contentStyle={chartTooltipStyle}
                  formatter={(v) => [`${Number(v).toFixed(2)}x`, 'ROAS']}
                  labelFormatter={(l) => l}
                />
              </LineChart>
            </ResponsiveContainer>
          ) : null}
        </div>
        <div className="tw-hero-substats">
          <div className="tw-hero-substat">
            <span className="tw-hero-substat-label">Revenue</span>
            <span className="tw-hero-substat-value">{moneyFmt(revenue, currency)}</span>
          </div>
          <div className="tw-hero-substat">
            <span className="tw-hero-substat-label">Ad spend</span>
            <span className="tw-hero-substat-value">{moneyFmt(adSpend, currency)}</span>
          </div>
          <div className="tw-hero-substat">
            <span className="tw-hero-substat-label">MER</span>
            <span className="tw-hero-substat-value">{pctFmt(merVal)}</span>
          </div>
        </div>
      </div>

      <div className="tw-hero-right">
        <div className="tw-gauge">
          <svg viewBox="0 0 200 110" className="tw-gauge-svg">
            {/* Background arc with segmented bands */}
            <path d="M 18 100 A 82 82 0 0 1 73 23"   stroke="rgba(248,113,113,0.35)" strokeWidth="14" fill="none" strokeLinecap="round" />
            <path d="M 73 23 A 82 82 0 0 1 127 23"   stroke="rgba(251,191,36,0.35)"  strokeWidth="14" fill="none" />
            <path d="M 127 23 A 82 82 0 0 1 182 100" stroke="rgba(16,185,129,0.35)"  strokeWidth="14" fill="none" strokeLinecap="round" />

            {/* Needle */}
            <g style={{ transformOrigin: '100px 100px', transform: `rotate(${angle - 90}deg)`, transition: 'transform 1.2s cubic-bezier(.2,.8,.2,1)' }}>
              <line x1="100" y1="100" x2="100" y2="28" stroke={gaugeColor} strokeWidth="3" strokeLinecap="round" />
              <circle cx="100" cy="100" r="6" fill={gaugeColor} />
            </g>

            {/* Tick labels */}
            <text x="18"  y="110" fill="rgba(255,255,255,0.4)" fontSize="9" textAnchor="middle">0</text>
            <text x="100" y="14"  fill="rgba(255,255,255,0.4)" fontSize="9" textAnchor="middle">{(target/2).toFixed(0)}x</text>
            <text x="182" y="110" fill="rgba(255,255,255,0.4)" fontSize="9" textAnchor="middle">{target}x+</text>
          </svg>
          <div className="tw-gauge-caption">
            {roas >= 3   ? '🟢 Profitable'  :
             roas >= 1.5 ? '🟡 Watch zone' :
                           '🔴 Below break-even'}
          </div>
        </div>
      </div>
    </motion.div>
  );
};

// ============================================================
// Sparkline tile — KPI value + tiny inline chart
// ============================================================
const SparkTile = ({ label, value, delta, series = [], color = '#10b981', icon, invertDelta = false, variant = 'line' }) => {
  const data = (series || []).map((y, i) => ({ i, y: Number(y) || 0 }));
  return (
    <motion.div
      className="tw-spark-tile"
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.04 }}
    >
      <div className="tw-spark-head">
        <span className="tw-spark-icon" style={{ color, background: `${color}22` }}>{icon}</span>
        <span className="tw-spark-label">{label}</span>
        <DeltaPill delta={delta} invertDelta={invertDelta} compact />
      </div>
      <div className="tw-spark-value">{value}</div>
      <div className="tw-spark-chart">
        {data.length > 1 && (
          <ResponsiveContainer width="100%" height={40}>
            {variant === 'bar' ? (
              <BarChart data={data}>
                <Bar dataKey="y" fill={color} radius={[2, 2, 0, 0]} />
                <Tooltip cursor={{ fill: 'rgba(255,255,255,0.05)' }} contentStyle={chartTooltipStyle} formatter={(v) => Number(v).toLocaleString()} />
              </BarChart>
            ) : (
              <LineChart data={data}>
                <defs>
                  <linearGradient id={`spark-${label.replace(/\W/g, '')}`} x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%"   stopColor={color} stopOpacity={0.5} />
                    <stop offset="100%" stopColor={color} stopOpacity={0} />
                  </linearGradient>
                </defs>
                <Line type="monotone" dataKey="y" stroke={color} strokeWidth={1.8} dot={false} fill={`url(#spark-${label.replace(/\W/g, '')})`} />
                <Tooltip contentStyle={chartTooltipStyle} formatter={(v) => Number(v).toLocaleString()} />
              </LineChart>
            )}
          </ResponsiveContainer>
        )}
      </div>
    </motion.div>
  );
};

// ============================================================
// Funnel widget — ATC -> Checkout -> Orders
// ============================================================
const FunnelWidget = ({ funnel }) => {
  const atc = Number(funnel?.atc) || 0;
  const checkout = Number(funnel?.checkout) || 0;
  const orders = Number(funnel?.orders) || 0;
  const top = Math.max(atc, checkout, orders, 1);
  const stages = [
    { key: 'atc',      label: 'Add to cart sessions', value: atc,      color: '#38bdf8', icon: <ShoppingCart size={14} /> },
    { key: 'checkout', label: 'Checkout sessions',    value: checkout, color: '#a78bfa', icon: <CreditCard size={14} /> },
    { key: 'orders',   label: 'Orders placed',        value: orders,   color: '#10b981', icon: <ShoppingBag size={14} /> },
  ];

  return (
    <div className="shop-panel tw-funnel-panel">
      <div className="shop-panel-head">
        <h3>Pixel funnel</h3>
        <span className="shop-panel-meta">window total</span>
      </div>
      {stages.every(s => s.value === 0) ? (
        <div className="shop-empty">No pixel events recorded.</div>
      ) : (
        <div className="tw-funnel-list">
          {stages.map((s, i) => {
            const widthPct = top > 0 ? (s.value / top) * 100 : 0;
            const dropFromPrev = i > 0 && stages[i-1].value > 0
              ? ((s.value - stages[i-1].value) / stages[i-1].value) * 100
              : null;
            return (
              <div key={s.key} className="tw-funnel-stage">
                {dropFromPrev != null && (
                  <div className="tw-funnel-drop">
                    {dropFromPrev < 0 ? '↓' : '↑'} {Math.abs(dropFromPrev).toFixed(1)}%
                  </div>
                )}
                <div className="tw-funnel-row">
                  <span className="tw-funnel-icon" style={{ color: s.color, background: `${s.color}22` }}>{s.icon}</span>
                  <div className="tw-funnel-content">
                    <div className="tw-funnel-meta">
                      <span className="tw-funnel-label">{s.label}</span>
                      <span className="tw-funnel-value">{intFmt(s.value)}</span>
                    </div>
                    <div className="tw-funnel-bar-wrap">
                      <div className="tw-funnel-bar-fill" style={{ width: `${widthPct}%`, background: `linear-gradient(90deg, ${s.color}, ${s.color}aa)` }} />
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

// ============================================================
// Customer-mix donut — new vs returning revenue, CAC in center
// ============================================================
const CustomerMixDonut = ({ mix, currency }) => {
  const newRev = Number(mix?.newRevenue) || 0;
  const retRev = Number(mix?.returningRevenue) || 0;
  const cac = Number(mix?.cac) || 0;
  const total = newRev + retRev;
  const data = [
    { name: 'New',       value: newRev, color: '#38bdf8' },
    { name: 'Returning', value: retRev, color: '#a78bfa' },
  ];
  return (
    <div className="shop-panel tw-mix-panel">
      <div className="shop-panel-head">
        <h3>Customer mix</h3>
        <span className="shop-panel-meta">revenue split</span>
      </div>
      {total === 0 ? (
        <div className="shop-empty">No revenue in window.</div>
      ) : (
        <>
          <div className="tw-mix-donut-wrap">
            <ResponsiveContainer width="100%" height={180}>
              <PieChart>
                <Pie data={data} dataKey="value" innerRadius={56} outerRadius={80} paddingAngle={3} startAngle={90} endAngle={450}>
                  {data.map((d) => <Cell key={d.name} fill={d.color} />)}
                </Pie>
                <Tooltip contentStyle={chartTooltipStyle} formatter={(v) => moneyFmt(v, currency)} />
              </PieChart>
            </ResponsiveContainer>
            <div className="tw-mix-center">
              <div className="tw-mix-center-label">CAC</div>
              <div className="tw-mix-center-value">{moneyFmt(cac, currency)}</div>
            </div>
          </div>
          <div className="tw-mix-legend">
            {data.map(d => (
              <div key={d.name} className="tw-mix-legend-row">
                <span className="tw-mix-legend-dot" style={{ background: d.color }} />
                <span className="tw-mix-legend-name">{d.name}</span>
                <span className="tw-mix-legend-pct">{total > 0 ? ((d.value / total) * 100).toFixed(0) : 0}%</span>
                <span className="tw-mix-legend-amount">{moneyFmt(d.value, currency)}</span>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

// ============================================================
// Profit widget — gross profit ratio bar
// ============================================================
const ProfitWidget = ({ revenue, grossProfit, currency }) => {
  const r = Number(revenue) || 0;
  const gp = Number(grossProfit) || 0;
  const margin = r > 0 ? (gp / r) * 100 : 0;
  const marginColor = margin >= 40 ? '#10b981' : margin >= 20 ? '#fbbf24' : '#f87171';
  return (
    <div className="shop-panel tw-profit-panel">
      <div className="shop-panel-head">
        <h3>Profit</h3>
        <span className="shop-panel-meta">window</span>
      </div>
      <div className="tw-profit-big" style={{ color: marginColor }}>
        {moneyFmt(gp, currency)}
      </div>
      <div className="tw-profit-margin">
        <span style={{ color: marginColor }}>{margin.toFixed(1)}%</span> net margin
      </div>
      <div className="tw-profit-bar">
        <div
          className="tw-profit-bar-fill"
          style={{ width: `${Math.min(margin, 100)}%`, background: `linear-gradient(90deg, ${marginColor}, ${marginColor}aa)` }}
        />
      </div>
      <div className="tw-profit-rev">
        of {moneyFmt(r, currency)} revenue
      </div>
    </div>
  );
};

// ============================================================
// Delta pill — colored chip with arrow
// ============================================================
const DeltaPill = ({ delta, invertDelta = false, compact = false }) => {
  if (delta == null) return null;
  const up = delta > 0;
  const meaningful = Math.abs(delta) >= 0.001;
  const positive = invertDelta ? !up : up;
  const cls = !meaningful ? 'flat' : (positive ? 'up' : 'down');
  return (
    <span className={`tw-delta-pill ${cls} ${compact ? 'compact' : ''}`}>
      {meaningful ? (up ? <ArrowUp size={11} /> : <ArrowDown size={11} />) : null}
      {meaningful ? `${(Math.abs(delta) * 100).toFixed(1)}%` : 'flat'}
    </span>
  );
};


const chartTooltipStyle = {
  background: 'rgba(20,20,25,0.95)',
  border: '1px solid rgba(255,255,255,0.12)',
  borderRadius: 8,
  color: '#fff',
  fontSize: 12,
};

export default TripleWhale;
